import {Controller} from 'stimulus';
import {overrideLinkTargets} from "../../../public/common";
import moment from 'moment';
import axios from 'axios';
import { get } from 'lodash';
import consumer from '../../../channels/consumer';
import feather from "feather-icons";

export default class extends Controller {
  static targets = ['form', 'deliveryDate', 'deliveryOn', 'deliveryDateField', 'deliveryDateFieldAlt', 'details', 'message', 'deliveryTypeTab', 'deliveryTypeError', 'alertDynamic', 'loaderTemplate'];
  static values = { cardId: Number };

  messageTimeout = 8; //sec
  messageTimer = null;

  initialize() {
    if (this.hasDetailsTarget) {
      overrideLinkTargets({
        $containerEl: this.detailsTarget
      });
    }

    if (this.hasMessageTarget) {
      this.setDefaultMessage();
    }

    if (this.hasDeliveryDateFieldAltTarget && this.hasDeliveryDateFieldTarget) {
      this.updateDeliveryDate();
    }

    if (this.hasFormTarget) {
      this.restorePreviousTab();
      this.connectToPDFChannel();
    }

    feather.replace({ width: 18, height: 18 });
  }

  gtagTrackLocker(eventName, gtagData) {
    if (window.gtag && gtagData) {
      const parsedGtagData = JSON.parse(gtagData || '{}');

      gtag('event', eventName, {
        event_category: 'Locker Flow',
        card: parsedGtagData.card,
        merchant: parsedGtagData.merchant
      });
    }
  }

  showDynamicError(message) {
    if (!message || !this.hasAlertDynamicTarget)
      return;

    this.alertDynamicTarget.querySelector('span').innerText = message;
    this.alertDynamicTarget.classList.remove('alert-info');
    this.alertDynamicTarget.classList.add('show', 'alert-danger');

    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
    }

    this.messageTimer = setTimeout(() => {
      this.hideDynamicError();
    }, this.messageTimeout * 1000);
  }

  hideDynamicError() {
    this.alertDynamicTarget.classList.remove('show');
  }

  showFormErrors(errors, $fields) {
    Object.keys(errors).forEach((key) => {
      const $input = [...$fields].find($input => $input.name.includes(key));

      if ($input) {
        const $errorContainer = $input.closest('.form-group')?.querySelector('.invalid-feedback');
        $input.classList.add('is-invalid');

        if ($errorContainer) {
          $errorContainer.innerText = errors[key].join(', ');
        }
      }
    });
  }

  hideFormErrors($fields) {
    $fields.forEach($input => {
      $input.classList.remove('is-invalid');
      const $errorContainer = $input.closest('.form-group')?.querySelector('.invalid-feedback');
      if ($errorContainer) {
        $errorContainer.innerText = '';
      }
    });
  }

  toggleFormLoader($form, isLoading) {
    // apply for visible elements only
    const $inputs = [...$form.querySelectorAll('input, textarea, a')]
      .filter($item => {
        const sizes = $item.getBoundingClientRect();
        return sizes.width > 0 && sizes.height > 0;
      });

    if (isLoading) {
      [...$inputs].forEach($input => {
        $input.setAttribute('disabled', 'disabled');
        if ($input.dataset.disableWith) {
          $input.dataset.defaultValue = $input.value;
          $input.value = $input.dataset.disableWith;
        }
      });
    } else {
      [...$inputs].forEach($input => {
        $input.removeAttribute('disabled');
        if ($input.dataset.defaultValue) {
          $input.value = $input.dataset.defaultValue;
        }
      });
    }
  }

  isPdfReady = false;

  showPdfLoader() {
    if (!this.hasAlertDynamicTarget || !this.hasLoaderTemplateTarget)
      return;

    this.alertDynamicTarget.querySelector('span').innerHTML = this.loaderTemplateTarget.innerHTML;
    this.alertDynamicTarget.classList.remove('alert-danger');
    this.alertDynamicTarget.classList.add('show', 'alert-info');
  }

  hidePdfLoader() {
    if (!this.hasAlertDynamicTarget) {
      return;
    }

    this.alertDynamicTarget.classList.remove('show', 'alert-info');
  }

  initPdfSpinnerTicker() {
    setTimeout(() => {
      if (!this.isPdfReady) {
        this.showPdfLoader()
      }
    }, this.messageTimeout * 0.5 * 1000);
  }

  submitPrintForm(e) {
    e.preventDefault();
    e.stopPropagation();

    const $target = e.currentTarget;
    const url = $target.dataset.pdfActionUrl;
    const $fields = $target.querySelectorAll('input[type=text], input[type=hidden], textarea, input[type=checkbox]:checked');

    const fd = new FormData();
    [...$fields]
      .filter(item => !item.disabled)
      .forEach(({name, value})  => {
        fd.has(name) ? fd.set(name, value) : fd.append(name, value);
      })

    this.toggleFormLoader($target, true);
    this.hideFormErrors($fields);
    this.gtagTrackLocker('print_gift', $target.dataset.gtag);

    axios.post(url, fd, {
      headers: {
        'x-csrf-token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
    })
      .then(() => {
        this.initPdfSpinnerTicker();
      })
      .catch((error) => {
      const errors = get(error.response, 'data', {});
      if (typeof errors.error === 'string') {
        // show generic error
        this.showDynamicError(errors.error);
      } else if (Math.floor(error.response.status / 100) === 5) {
        // handle any 5xx errors
        this.showDynamicError(window.translations.error_something_went_wrong);
      } else {
        // show field errors
        this.showFormErrors(errors, $fields);
      }
      this.toggleFormLoader($target, false);
    });
  }

  forceSubmitEmailForm() {
    this.formTarget.submit();

    // emulate $nextTick
    setTimeout(() => {
      this.toggleFormLoader(this.formTarget, true);
    }, 1);
  }

  submitEmailForm(e) {
    e.preventDefault();
    e.stopPropagation();

    const $target = e.currentTarget;
    const $deliveryOnTarget = this.deliveryOnTargets.find(item => item.checked)
    const isSendNow = $deliveryOnTarget.value === 'true';

    if (isSendNow) {
      const message = this.deliveryDateTarget.dataset.altHint;

      if (confirm(message)) {
        this.gtagTrackLocker('gift_now', $target.dataset.gtag);
        this.forceSubmitEmailForm();
      }
    } else {
      this.gtagTrackLocker('schedule_gift', $target.dataset.gtag);
      this.forceSubmitEmailForm();
    }
  }

  submitForm(e) {
    e.preventDefault();
    e.stopPropagation();

   // check if delivery type is selected and show error if not
    const deliveryType = this.deliveryTypeTabTargets.find(item => item.classList.contains('active'));
    if (!deliveryType) {
      this.deliveryTypeErrorTarget.classList.remove('d-none');
      return;
    }

    const type = deliveryType.getAttribute('aria-controls');
    if ((/^email/).test(type)) {
      this.submitEmailForm(e);
    } else {
      this.submitPrintForm(e);
    }
  }

  changeDeliveryType(e) {
    const $target = e.currentTarget;
    const action = $target.getAttribute('aria-controls').split('_')[0];
    const $form = $target.closest('form');

    this.deliveryTypeErrorTarget.classList.add('d-none');

    const $submitBtn = $form.querySelector('input[type=submit]');
    $submitBtn.value = $submitBtn.dataset[`${action}DefaultValue`];

    // disable inputs inside the sibling tabs to prevent send it to BE
    const siblings = [...$target.closest('ul').querySelectorAll('a')].filter(item => item!== $target);
    siblings.forEach(item => {
      const $oneTab = document.getElementById(item.getAttribute('aria-controls'));
      const $inputs = $oneTab.querySelectorAll('input');
      [...$inputs].forEach($input => {
        $input.setAttribute('disabled', 'disabled');
        $input.removeAttribute('required');
      });
    });

    // enable inputs inside the selected tab
    const $inputs = document.getElementById($target.getAttribute('aria-controls')).querySelectorAll('input');
    [...$inputs].forEach($input => {
      $input.removeAttribute('disabled');

      if ($input.dataset.required) {
        $input.setAttribute('required', 'required');
      }
    });

    // store what is selected to restore it on page reload on error state
    const formId = $form.dataset.backUrl.match(/\d+$/)[0];
    sessionStorage.setItem(`delivery_${formId}`, $target.id);
  }

  restorePreviousTab() {
    const formId = this.formTarget.dataset.backUrl.match(/\d+$/)[0];
    const hasInvalidFields = !!this.formTarget.querySelector('.is-invalid');

    if (hasInvalidFields) {
      const $target = document.getElementById(sessionStorage.getItem(`delivery_${formId}`));
      $target && $target.click();
    } else {
      sessionStorage.clear();
    }
  }

  changeDeliveryOn(e) {
    const target = e.currentTarget;
    const isSendNow = target.value === 'true';

    this.deliveryDateTarget.classList.toggle('d-none', isSendNow);
    const $input = this.deliveryDateTarget.getElementsByTagName('input')[0];

    if (isSendNow) {
      $input.removeAttribute('required');
    } else {
      $input.setAttribute('required', 'required');
    }
  }

  updateCountdown(e) {
    const $message = e.currentTarget;
    const { value, maxLength } = $message;
    const $countdown = $message.closest('form').querySelector('.countdown span');

    if (value.length > maxLength) {
      $message.value = value.substring(0, maxLength);
    } else {
      $countdown.innerText = maxLength - value.length;
    }
  }

  setDefaultMessage() {
    [...this.messageTargets].forEach($message => {
      const { value, dataset: {defaultMessage} } = $message;

      if (!value) {
        $message.value = defaultMessage;
      }
      this.updateCountdown({currentTarget: $message});
    });
  }

  updateDeliveryDate() {
    const localeOptions = {
      en: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      ja: {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    };

    const locale = document.documentElement.getAttribute('lang');
    const localeSettings = localeOptions[locale] || localeOptions.en;

    const $alt = this.deliveryDateFieldAltTarget;
    const $target = this.deliveryDateFieldTarget;
    const {value} = $target;

    $alt.value = value ? moment(value).toDate().toLocaleDateString(locale, localeSettings) : '';
  }


  focusOnAltDeliveryDate(e) {
    e.preventDefault();
    e.stopPropagation();

    this.deliveryDateFieldTarget.showPicker();
  }

  clickOnAltDeliveryDate(e) {
    this.deliveryDateFieldTarget.focus();
  }

  connectToPDFChannel() {
    consumer.subscriptions.create({ channel: 'Ia3::Common::GiftPDFChannel', id: this.cardIdValue }, {
      received: (result) => {
        this.toggleFormLoader(this.formTarget, false);
        if (result) {
          this.handlePrintSuccess(result);
        } else {
          this.showDynamicError(window.translations.error_something_went_wrong);
        }
      }
    });
  }

  handlePrintSuccess(pdfUrl) {
    this.isPdfReady = true;
    this.hidePdfLoader();

    const windowRef = window.open(pdfUrl, '_blank');
    if (!windowRef) {
      alert(window.translations.blocked_popup_warning);
    }

    const backUrl = this.formTarget.dataset.backUrl;
    if (backUrl) {
      const [path, hash] = backUrl.split('#');
      if (hash) {
        location.hash = hash;
      }
      location.pathname = path;
    } else {
      location.reload();
    }
  }
}
